import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Icon } from '../atoms/Icon';
import { cn } from '../../lib/utils';
import { getIntakeUrl } from '../../config/contact';
import { useMetroStore } from '../../stores/metroStore';
import { useAuthStore } from '../../stores/authStore';
import MetroBadge from '../molecules/MetroBadge';
import { useSavedNames } from '../saved/savedListUtils';

// Badge count sources from the canonical save path (savedListUtils):
// every mutation dispatches savedApartmentsChanged, so the old 2s
// polling fallback is gone.
function useSavedCount() {
  return useSavedNames().length;
}

/**
 * NavigationBar organism - Top navigation for desktop
 * Brand Design: Black header with amber accents for consistency across light/dark modes
 */
export function NavigationBar({ currentPage, className, transparent, scopeSummary, onEditScope }: { currentPage?: string; className?: string; transparent?: boolean; scopeSummary?: string; onEditScope?: () => void }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const savedCount = useSavedCount();
  const selectedMetro = useMetroStore((s) => s.selectedMetro);

  const navItems = [
    { id: 'search', label: 'Apartments', to: '/search', icon: 'search' },
    { id: 'vibe', label: 'By Vibe', to: '/vibe', icon: 'message-circle' },
    { id: 'explore', label: 'Map', to: '/browse', icon: 'map-pin' },
    { id: 'saved', label: 'Saved', to: '/my-list', icon: 'heart', badge: savedCount },
    { id: 'dashboard', label: 'Dashboard', to: '/dashboard', icon: 'layout-dashboard', auth: true },
    { id: 'admin', label: 'Admin', to: '/admin', icon: 'shield', admin: true },
  ];

  const visibleItems = navItems.filter((i) => {
    if (i.admin) return !!user?.is_admin;
    if (i.auth) return !!user;
    return true;
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header
      className={cn(
        transparent ? 'absolute top-0 left-0 right-0 z-50 w-full border-b-0' : 'sticky top-0 z-50 w-full border-b',
        className
      )}
      data-testid="navigation-bar"
      style={transparent ? { background: 'transparent', borderBottom: 'none', color: 'var(--ts-anchor-text)' } : {
        background: 'rgba(8, 8, 14, 0.82)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--ts-anchor-border)',
        color: 'var(--ts-anchor-text)',
      }}
    >
      <div className="container flex h-16 items-center justify-between px-4 md:px-8">
        {/* Logo & Home Link */}
        <div className="flex items-center gap-4">
          {/* Logo (links home; replaces the removed redundant home-square button) */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span role="img" aria-label="Taco" style={{ fontSize: 26, lineHeight: 1, display: 'inline-block' }}>🌮</span>
            <span
              className="hidden sm:inline"
              style={{ font: '700 17px/1 var(--ts-font-display)', letterSpacing: '-0.01em', color: 'var(--ts-anchor-text)' }}
            >
              Taco Street Locating <span className="ts-ai-badge ts-ai-badge--on-dark" aria-label="AI" style={{ fontSize: '0.92em', marginLeft: '0.12em' }}>AI</span>
            </span>
            <span className="sm:hidden" style={{ font: '700 15px/1 var(--ts-font-display)', color: 'var(--ts-anchor-text)' }}>Taco Street Locating <span className="ts-ai-badge ts-ai-badge--on-dark" aria-label="AI" style={{ fontSize: '0.92em', marginLeft: '0.12em' }}>AI</span></span>
          </Link>
          <MetroBadge className="hidden md:inline-flex" />
        </div>

        {/* Scoped-search glass field (board 5JK-0) — shows the active search
            scope on results surfaces; tapping it re-opens the criteria flow. */}
        {scopeSummary && (
          <button
            // Pages that own richer search state (the browse map) pass
            // onEditScope to seed the wizard before the jump — otherwise the
            // wizard opens with its own defaults and the user's scope is lost.
            onClick={() => (onEditScope ? onEditScope() : navigate('/search'))}
            data-testid="nav-scope-summary"
            className="hidden xl:flex items-center gap-2.5 flex-shrink min-w-0 text-left"
            style={{
              width: 380,
              padding: '9px 14px',
              borderRadius: 10,
              backgroundImage: 'linear-gradient(180deg, #363026 0%, #262232 100%)',
              border: '1px solid rgba(255, 200, 60, 0.4)',
              boxShadow: '0 0 16px rgba(255, 190, 40, 0.14)',
              cursor: 'pointer',
            }}
            title="Edit your search"
          >
            <span style={{ fontFamily: 'var(--ts-font-ui)', fontSize: 13, fontWeight: 700, color: 'var(--ts-accent-primary)' }} aria-hidden>⌕</span>
            <span className="truncate" style={{ fontSize: 13, fontWeight: 500, color: 'var(--ts-text-secondary)' }}>
              {scopeSummary}
            </span>
          </button>
        )}

        {/* Desktop Nav */}
        <nav className="hidden xl:flex items-center gap-1 whitespace-nowrap">
          <details className="relative" onKeyDown={event => { if (event.key === 'Escape') event.currentTarget.open = false; }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) event.currentTarget.open = false; }}>
            <summary className="cursor-pointer px-3 py-2 rounded-full text-sm font-semibold">Neighborhood Guides</summary>
            <div className="absolute left-0 top-full mt-2 w-64 rounded-xl border p-2 shadow-xl" style={{background:'#14121a',borderColor:'var(--ts-anchor-border)'}}>
              <Link className="block rounded-lg px-3 py-3 hover:bg-white/10" to="/n/austin/downtown" onClick={event => { event.currentTarget.closest('details')?.removeAttribute('open'); }}>Downtown Austin</Link>
              <Link className="block rounded-lg px-3 py-3 hover:bg-white/10" to="/n/austin/east-austin" onClick={event => { event.currentTarget.closest('details')?.removeAttribute('open'); }}>East Austin</Link>
              <Link className="block rounded-lg px-3 py-3 hover:bg-white/10" to="/n/austin/south-austin" onClick={event => { event.currentTarget.closest('details')?.removeAttribute('open'); }}>South Austin</Link>
              <Link className="block rounded-lg px-3 py-3 hover:bg-white/10 text-sm" to="/explore" onClick={event => { event.currentTarget.closest('details')?.removeAttribute('open'); }}>Explore all neighborhoods →</Link>
            </div>
          </details>
          {visibleItems.map((item) => {
            const isActive = currentPage === item.id;
            const commonClass = cn(
              'px-3.5 py-2 rounded-full transition-colors relative',
              !isActive && (transparent ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-[color:var(--ts-anchor-text-muted)] hover:text-[color:var(--ts-anchor-text)] hover:bg-white/10')
            );
            // T4 — mono = label, never control. Nav links are Inter sentence
            // case inside the same pill shape.
            const hudFont: React.CSSProperties = {
              fontFamily: 'var(--ts-font-ui)',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: 0,
            };
            return (
              <Link
                key={item.id}
                to={item.to}
                className={commonClass}
                style={isActive
                  ? { ...hudFont, background: 'color-mix(in srgb, var(--ts-accent-primary) 22%, transparent)', color: 'var(--ts-accent-primary)', textShadow: '0 0 10px rgba(255, 200, 60, 0.5)' }
                  : hudFont}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
                {(item.badge ?? 0) > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold px-1" style={{ background: 'var(--ts-accent-primary)', color: 'var(--ts-accent-contrast)', boxShadow: '0 0 10px var(--ts-accent-glow)' }} data-testid="saved-count-badge">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
          <a
            href={getIntakeUrl(selectedMetro)}
            target="_blank"
            rel="noopener noreferrer"
            className={cn('px-3.5 py-2 rounded-full transition-colors', transparent ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-[color:var(--ts-anchor-text-muted)] hover:text-[color:var(--ts-anchor-text)] hover:bg-white/10')}
            style={{ fontFamily: 'var(--ts-font-ui)', fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,210,77,0.35)' }}
            data-testid="nav-contact"
          >
            Talk to a human
          </a>
        </nav>

        {/* Right Section */}
        <div className="flex items-center gap-3">
          {/* Neon HUD status — AI LIVE */}
          <span
            className="hidden 2xl:inline-flex items-center gap-1.5 mr-1"
            style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'var(--ts-accent-primary)', textShadow: '0 0 10px rgba(255, 200, 60, 0.55)' }}
            aria-hidden="true"
          >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--ts-accent-primary)', boxShadow: '0 0 8px rgba(255, 200, 60, 0.9)', animation: 'ts-pulse 2s ease-in-out infinite' }} />
            AI LIVE
          </span>
          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-[color:var(--ts-anchor-text-muted)] hidden sm:inline">{user?.name || user?.email}</span>
              <Avatar className="h-8 w-8 ring-2 ring-[color:var(--ts-accent-primary)]">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[color:var(--ts-accent-muted)] text-[color:var(--ts-accent-text)]">
                  {(user?.name || user?.email || 'U')[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout} 
                className="text-[color:var(--ts-anchor-text-muted)] hover:text-[color:var(--ts-anchor-text)] hover:bg-white/10"
                data-testid="nav-logout"
              >
                <Icon name="log-out" size="sm" />
              </Button>
            </div>
          ) : (
            <Link to="/login">
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full min-h-[44px] px-4 font-semibold hover:bg-[color:var(--ts-accent-muted)]"
                style={{ background: 'transparent', border: '1px solid rgba(255, 210, 77, 0.5)', color: 'var(--ts-accent-text)' }}
                data-testid="nav-signin"
              >
                Sign In
              </Button>
            </Link>
          )}

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="xl:hidden">
              <Button aria-label="Open navigation" variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] text-[color:var(--ts-anchor-text)] hover:bg-white/10">
                <Icon name="menu" size="md" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-[color:var(--ts-anchor)] border-[color:var(--ts-anchor-border)]">
              <nav className="flex flex-col gap-2 mt-8">
                <p className="px-4 text-sm font-semibold">Neighborhood Guides</p>
                <SheetClose asChild><Link to="/n/austin/downtown" className="block px-4 py-3 rounded-lg hover:bg-white/10">Downtown Austin</Link></SheetClose>
                <SheetClose asChild><Link to="/n/austin/east-austin" className="block px-4 py-3 rounded-lg hover:bg-white/10">East Austin</Link></SheetClose>
                <SheetClose asChild><Link to="/n/austin/south-austin" className="block px-4 py-3 rounded-lg hover:bg-white/10">South Austin</Link></SheetClose>
                <SheetClose asChild><Link to="/explore" className="block px-4 py-3 rounded-lg hover:bg-white/10">Explore all neighborhoods →</Link></SheetClose>
                {/* Home link to main site */}
                <a
                  href="https://tacostreetlocating.com"
                  className="flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-lg text-[color:var(--ts-anchor-text-muted)] hover:text-[color:var(--ts-anchor-text)] hover:bg-white/10 transition-colors"
                >
                  <Icon name="home" size="sm" />
                  Main Website
                </a>
                <div className="h-px bg-[color:var(--ts-anchor-border)] my-2" />
                {visibleItems.map((item) => (
                  <Link
                    key={item.id}
                    to={item.to}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-lg transition-colors active:bg-white/5',
                      currentPage === item.id
                        ? 'bg-[color:var(--ts-accent-muted)] text-[color:var(--ts-accent-text)]'
                        : 'text-[color:var(--ts-anchor-text-muted)] hover:text-[color:var(--ts-anchor-text)] hover:bg-white/10'
                    )}
                  >
                    <Icon name={item.icon} size="sm" />
                    {item.label}
                  </Link>
                ))}
                <a
                  href={getIntakeUrl(selectedMetro)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-[color:var(--ts-anchor-text-muted)] hover:bg-white/10"
                  style={{ fontFamily: 'var(--ts-font-ui)', fontSize: 14, fontWeight: 600 }}
                  data-testid="mobile-nav-contact"
                >
                  <Icon name="phone" size="sm" /> Talk to a human
                </a>
                {!user && (
                  <Link
                    to="/login"
                    className="flex items-center gap-3 px-4 py-3 min-h-[48px] rounded-lg hover:bg-white/10 active:bg-white/5 text-[color:var(--ts-accent-text)] font-medium"
                  >
                    <Icon name="log-in" size="sm" />
                    Sign In
                  </Link>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
